import type { CmsManifest, CmsManifestFile } from '@vastra/types';

export type Portal = 'admin' | 'customer';

export interface CmsI18nInitOptions {
  /** Where to fetch JSON from (API base for admin; store origin for customer). */
  baseUrl: string;
  /** Fetches the manifest JSON object. */
  fetchManifest: (baseUrl: string) => Promise<CmsManifest>;
  /** Fetches a single CMS file's content JSON object. */
  fetchFileContent: (baseUrl: string, file: CmsManifestFile) => Promise<Record<string, unknown>>;
}

let translations: Record<string, string> = {};
let loaded = false;
let loadingPromise: Promise<void> | null = null;
let version = 0;
const listeners = new Set<(v: number) => void>();

/** Bumped on reload/reset so background deferred merges never apply after a newer load started. */
let loadEpoch = 0;

/** Current manifest signature used for change detection (updatedAt + file count). Empty until first load. */
let manifestSignature = '';

/** Timer handle for poll loops (browser-only usage). */
let pollTimer: ReturnType<typeof setInterval> | null = null;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function flattenObject(
  obj: Record<string, unknown>,
  prefix = '',
  out: Record<string, string> = {},
): Record<string, string> {
  for (const [k, v] of Object.entries(obj)) {
    if (k.startsWith('_')) continue;
    const nextKey = prefix ? `${prefix}.${k}` : k;
    if (isPlainObject(v)) flattenObject(v, nextKey, out);
    else out[nextKey] = String(v ?? '');
  }
  return out;
}

function normalizeFileContentToFlatMap(fileId: string, raw: Record<string, unknown>): Record<string, string> {
  // Support both:
  // - Legacy flat maps: { "i18n.admin.common.en.foo": "bar" }
  // - Tree JSON: { "foo": { "bar": "baz" } } which becomes "i18n.admin.common.en.foo.bar" at runtime.
  // - Tamil (or other) override files may repeat the *English* lookup keys (`i18n.*.en.*`) so admin
  //   `t("…en…")` resolves when only the `locale: "ta"` bundle is loaded.
  const flat = flattenObject(raw);
  const flatKeys = Object.keys(flat);
  const wantsPrefix =
    flatKeys.some((k) => k.startsWith(`${fileId}.`)) ||
    (flatKeys.length > 0 && flatKeys.every((k) => k.startsWith('i18n.')));
  if (wantsPrefix) return flat;

  const prefixed: Record<string, string> = {};
  for (const [k, v] of Object.entries(flat)) prefixed[`${fileId}.${k}`] = v;
  return prefixed;
}

function pickI18nFiles(manifest: CmsManifest, portal: Portal, locale: string): CmsManifestFile[] {
  const files = (manifest.files ?? []).filter((f) => {
    if (!Array.isArray(f.portals) || !f.portals.includes(portal)) return false;
    if ((f.locale ?? '').trim() && String(f.locale).trim() !== locale) return false;
    if (Array.isArray(f.tags) && f.tags.length > 0 && !f.tags.includes('i18n')) return false;
    return true;
  });
  return [...files].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
}

/** Splits manifest entries; if every file is tagged `deferred`, load all in the first wave (avoid empty shell). */
function partitionDeferred(files: CmsManifestFile[]): { eager: CmsManifestFile[]; deferred: CmsManifestFile[] } {
  const eager = files.filter((f) => !(f.tags ?? []).includes('deferred'));
  const deferred = files.filter((f) => (f.tags ?? []).includes('deferred'));
  if (eager.length === 0 && deferred.length > 0) return { eager: deferred, deferred: [] };
  return { eager, deferred };
}

export async function mergeAdditionalCmsFiles(
  opts: CmsI18nInitOptions,
  portal: Portal,
  locale: string,
): Promise<void> {
  const baseUrl = opts.baseUrl.trim().replace(/\/$/, '');
  if (!baseUrl) return;
  try {
    const manifest = await opts.fetchManifest(baseUrl);
    const files = pickI18nFiles(manifest, portal, locale);
    if (files.length === 0) return;
    const add = await mergeFilesInOrder(baseUrl, files, opts.fetchFileContent);
    translations = { ...translations, ...add };
    bumpVersion();
  } catch {
    // keep existing strings
  }
}

async function mergeFilesInOrder(
  baseUrl: string,
  files: CmsManifestFile[],
  fetchFileContent: CmsI18nInitOptions['fetchFileContent'],
): Promise<Record<string, string>> {
  const flats = await Promise.all(
    files.map(async (f) => {
      const content = await fetchFileContent(baseUrl, f);
      return normalizeFileContentToFlatMap(f.id, content ?? {});
    }),
  );
  const merged: Record<string, string> = {};
  for (const flat of flats) {
    for (const [k, v] of Object.entries(flat)) merged[k] = v;
  }
  return merged;
}

export function resetCmsI18nForTests(): void {
  loadEpoch += 1;
  translations = {};
  loaded = false;
  loadingPromise = null;
  version = 0;
  listeners.clear();
  manifestSignature = '';
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function computeManifestSignature(manifest: CmsManifest): string {
  // `updatedAt` is the primary signal; file count guards against manifest-swap without stamp.
  return `${manifest.updatedAt ?? ''}::${(manifest.files ?? []).length}`;
}

export function getI18nVersion(): number {
  return version;
}

export function subscribeI18n(cb: (v: number) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function bumpVersion(): void {
  version += 1;
  for (const cb of listeners) cb(version);
}

export async function initCmsI18n(
  opts: CmsI18nInitOptions,
  portal: Portal,
  locale: string,
): Promise<void> {
  if (loaded) return;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    const epoch = loadEpoch;
    const baseUrl = opts.baseUrl.trim().replace(/\/$/, '');
    if (!baseUrl) {
      loaded = true;
      return;
    }
    let deferredBatch: CmsManifestFile[] = [];
    try {
      const manifest = await opts.fetchManifest(baseUrl);
      manifestSignature = computeManifestSignature(manifest);
      const files = pickI18nFiles(manifest, portal, locale);
      const { eager, deferred } = partitionDeferred(files);
      deferredBatch = deferred;

      translations = await mergeFilesInOrder(baseUrl, eager, opts.fetchFileContent);
      bumpVersion();
    } catch {
      translations = {};
      bumpVersion();
    } finally {
      loaded = true;
    }

    if (deferredBatch.length > 0) {
      const batch = deferredBatch;
      const runDeferred = async () => {
        if (epoch !== loadEpoch) return;
        try {
          const add = await mergeFilesInOrder(baseUrl, batch, opts.fetchFileContent);
          if (epoch !== loadEpoch) return;
          translations = { ...translations, ...add };
          bumpVersion();
        } catch {
          // keep eager-only strings
        }
      };
      if (typeof queueMicrotask === 'function') queueMicrotask(() => void runDeferred());
      else void Promise.resolve().then(runDeferred);
    }
  })();

  return loadingPromise;
}

export async function reloadCmsI18n(
  opts: CmsI18nInitOptions,
  portal: Portal,
  locale: string,
): Promise<void> {
  loadEpoch += 1;
  loaded = false;
  loadingPromise = null;
  return initCmsI18n(opts, portal, locale);
}

export function t(
  key: string,
  fallbackOrParams?: string | Record<string, unknown>,
  params?: Record<string, unknown>,
): string {
  const v = translations[key];
  let res = typeof v === 'string' && v.trim() ? v : undefined;
  let interpolations: Record<string, unknown> | undefined;

  if (typeof fallbackOrParams === 'string') {
    res = res ?? fallbackOrParams;
    interpolations = params;
  } else if (fallbackOrParams && typeof fallbackOrParams === 'object') {
    if (typeof fallbackOrParams['fallback'] === 'string') {
      res = res ?? fallbackOrParams['fallback'];
    }
    interpolations = fallbackOrParams;
  }

  res = res ?? key;
  if (interpolations) {
    for (const [k, val] of Object.entries(interpolations)) {
      if (k !== 'fallback' && val !== undefined) {
        res = res.replace(new RegExp(`\\{${k}\\}`, 'g'), String(val));
      }
    }
  }
  return res;
}

export interface StartManifestPollingOptions {
  /** Interval in ms. Default: 60_000. Polling pauses while the tab is hidden. */
  intervalMs?: number;
  /** Skip network when the document is hidden. Default: true. */
  pauseWhenHidden?: boolean;
  /**
   * When the manifest signature changes, run this instead of `reloadCmsI18n`
   * (e.g. admin loads `en` then merges a `ta` overlay).
   */
  customReload?: () => Promise<void>;
}

/**
 * Start a background poll that refetches the manifest and, if its signature
 * (`updatedAt + files.length`) changed since the last load, reloads i18n.
 * Returns an unsubscribe function. Safe to call multiple times — later calls
 * replace the earlier timer. Browser-only; no-op in non-browser envs.
 */
export function startCmsI18nManifestPolling(
  opts: CmsI18nInitOptions,
  portal: Portal,
  locale: string,
  options: StartManifestPollingOptions = {},
): () => void {
  if (typeof window === 'undefined' || typeof setInterval === 'undefined') {
    return () => {};
  }
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }

  const intervalMs = Math.max(5_000, options.intervalMs ?? 60_000);
  const pauseWhenHidden = options.pauseWhenHidden ?? true;
  const baseUrl = opts.baseUrl.trim().replace(/\/$/, '');
  if (!baseUrl) return () => {};

  const checkOnce = async (): Promise<void> => {
    if (pauseWhenHidden && typeof document !== 'undefined' && document.hidden) return;
    try {
      const manifest = await opts.fetchManifest(baseUrl);
      const next = computeManifestSignature(manifest);
      if (next && next !== manifestSignature) {
        if (options.customReload) await options.customReload();
        else await reloadCmsI18n(opts, portal, locale);
      }
    } catch {
      // Swallow — next tick will retry.
    }
  };

  const onVisibility = (): void => {
    if (typeof document === 'undefined') return;
    if (!document.hidden) void checkOnce();
  };

  pollTimer = setInterval(() => void checkOnce(), intervalMs);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
  }

  return () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibility);
    }
  };
}

